import React, { createRef, useState } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import FormHelperText from "@mui/material/FormHelperText";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import UploadIcon from "@mui/icons-material/Upload";
import DoneIcon from "@mui/icons-material/Done";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface FileData {
  id: string;
  url: string;
}

interface FileUploadResponse {
  file: FileData;
}

interface FileQueryResponse {
  file: FileData;
}

interface DropzoneProps {
  accept?: string;
  deleteFileText?: string;
  fileUploadedText?: string;
  fileDeletedText?: string;
  width?: number | string;
  height?: number | string;
  videoWidth?: number;
  videoHeight?: number;
  onChange: (fileId: string | null) => void;
  label: string;
  helperText?: string;
  value?: string | null;
  required?: boolean;
  query: DocumentNode;
  mutation: DocumentNode;
  square?: boolean;
  maxSize?: number;
  /** Кнопка удаления под превью (below) или в плашке поверх изображения внизу (overlay) */
  deleteButtonPosition?: "below" | "overlay";
}

export function Dropzone({
  accept = "*",
  deleteFileText,
  fileUploadedText,
  fileDeletedText,
  width = 240,
  height = 240,
  videoWidth = 400,
  videoHeight,
  onChange,
  label,
  helperText,
  value,
  required,
  query,
  mutation,
  square,
  maxSize,
  deleteButtonPosition = "below",
}: DropzoneProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();

  const finalDeleteFileText =
    deleteFileText ||
    t("shared.dropzone.deleteFile", { defaultValue: "Удалить файл" });
  const finalFileUploadedText =
    fileUploadedText ||
    t("shared.dropzone.fileUploaded", { defaultValue: "Файл загружен" });
  const finalFileDeletedText =
    fileDeletedText ||
    t("shared.dropzone.fileDeleted", { defaultValue: "Файл удален" });

  const [drag, setDrag] = useState<boolean>(false);

  const [mutate, mutationResult] = useMutation<
    FileUploadResponse,
    { input: { file: File } }
  >(mutation, {
    onCompleted({ file }) {
      onChange(file.id);
      enqueueSnackbar(finalFileUploadedText, { variant: "success" });
    },
    onError(error) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const queryResult = useQuery<FileQueryResponse, { id: string }>(query, {
    variables: { id: value as string },
    skip: !value,
  });

  const inputRef = createRef<HTMLInputElement>();

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      validity: { valid },
      files,
    } = event.target;

    const file = files?.[0];

    if (!file) return;

    if (maxSize && file.size > maxSize) {
      return enqueueSnackbar(
        t("shared.dropzone.tooLarge", {
          defaultValue: "Размер файла превышает {{bytes}} байт",
          bytes: maxSize,
        }),
        { variant: "error" }
      );
    }

    if (valid) {
      mutate({ variables: { input: { file } } });
    }
  };

  const onDragEnter = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDrag(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDrag(false);
  };

  const onDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const file = event.dataTransfer.files[0];

    if (file) {
      mutate({ variables: { input: { file } } });
    }

    setDrag(false);
  };

  const onClickDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onChange(null);
    mutationResult.reset();
    enqueueSnackbar(finalFileDeletedText, { variant: "success" });
  };

  const loading = mutationResult.loading || queryResult.loading;
  const file = mutationResult.data?.file || queryResult.data?.file;
  const url = file?.url;

  return (
    <>
      <FormControl
        component="fieldset"
        fullWidth
        required={required}
        sx={{
          border: "1px solid",
          borderColor: theme.palette.divider,
          borderRadius: 1,
          padding: 1.5,
          position: "relative",
          alignItems: "center",
          "&:hover": {
            borderColor: theme.palette.text.secondary,
          },
        }}
      >
        {label && (
          <FormLabel
            component="legend"
            sx={{
              float: "unset",
              display: "block",
              width: "auto",
              padding: "0 5px",
              fontSize: "0.75em",
              height: 0,
              top: -8,
              maxWidth: "100%",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </FormLabel>
        )}

        <Box
          sx={{
            position: "relative",
            width: width,
            height: height,
            "&:hover .delete-overlay": {
              opacity: 1,
              pointerEvents: "auto",
            },
          }}
        >
          <ButtonBase
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.23)",
              borderRadius: square ? 0 : "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backgroundSize: square ? "contain" : "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundImage: url ? `url('${url}')` : undefined,
              fontSize: "5rem",
              color: drag ? "success.main" : "text.disabled",
              border: "2px solid",
              borderColor: drag || loading ? "#2e7d32" : "rgba(0, 0, 0, 0.38)",
              overflow: "hidden",
            }}
            onClick={onClick}
            onDragOverCapture={onDragOver}
            onDragEnterCapture={onDragEnter}
            onDragLeaveCapture={onDragLeave}
            onDropCapture={onDrop}
          >
            {loading ? (
              <CircularProgress
                sx={{ pointerEvents: "none" }}
                color="success"
              />
            ) : !url ? (
              <UploadIcon
                fontSize="inherit"
                color="inherit"
                sx={{ pointerEvents: "none" }}
              />
            ) : accept.startsWith("video") ? (
              <video
                src={url}
                style={{
                  width: "100%",
                  maxWidth: videoWidth,
                  maxHeight: videoHeight,
                }}
                controls
                playsInline
                muted
              />
            ) : mutationResult.data?.file ? (
              <DoneIcon
                fontSize="inherit"
                color="inherit"
                sx={{ pointerEvents: "none" }}
              />
            ) : null}
          </ButtonBase>

          {value && deleteButtonPosition === "overlay" && (
            <Box
              className="delete-overlay"
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                left: 0,
                height: 51,
                backgroundColor: "rgba(0,0,0,0.9)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                px: 1,
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title={finalDeleteFileText} placement="top">
                <IconButton
                  onClick={onClickDelete}
                  size="large"
                  color="inherit"
                  aria-label={finalDeleteFileText}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <input
          accept={accept}
          required={required && !value}
          type="file"
          style={{
            position: "absolute",
            opacity: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
          }}
          ref={inputRef}
          onChange={onChangeInput}
        />

        {value && deleteButtonPosition === "below" && (
          <Button
            onClick={onClickDelete}
            color="inherit"
            sx={{ mt: 2 }}
            fullWidth
          >
            {finalDeleteFileText}
          </Button>
        )}
      </FormControl>
      {helperText && (
        <FormHelperText
          style={{
            marginTop: theme.spacing(0.5),
            marginRight: theme.spacing(2),
            marginBottom: 0,
            marginLeft: theme.spacing(2),
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </>
  );
}
